import type { FC } from 'react';
import { memo } from 'react';
import { Link } from 'react-router-dom';

import useGaTracker from '../components/PageTracking';
import { fetchProperties } from '../components/data/SWR';
import useSeo from '../components/useSeo';
import { strapiUrl } from '../config/env';

interface PropertyProps {
  ejendom: {
    Adresse: string;
    Antal_Lejemaal: number;
    id: number;
    Billede: {
      formats?: {
        small?: {
          url: string;
        };
      };
      url: string;
    };
    Bygget: string;
  };
}

const PropertyCard: FC<{ property: PropertyProps }> = ({ property }) => {
  const imageUrl =
    property.ejendom.Billede?.formats?.small?.url || property.ejendom.Billede?.url || '';

  return (
    <Link to={`/ejendom/${property.ejendom.id}`}>
      <article className="property-card fade-in">
        <div className="property-card__media">
          {imageUrl ? (
            <img
              alt={`Billede af ${property.ejendom.Adresse}`}
              loading="lazy"
              src={`${strapiUrl}${imageUrl}`}
            />
          ) : null}
        </div>
        <div className="property-card__body">
          <h3>{property.ejendom.Adresse}</h3>
          <p>Udforsk en velholdt ejendom med attraktiv placering i Silkeborg.</p>
          <div className="property-card__meta">
            <span className="property-pill property-pill--muted">Opført {property.ejendom.Bygget}</span>
            <span className="property-pill">{property.ejendom.Antal_Lejemaal} lejemål</span>
          </div>
          <span className="property-link">
            Se ejendom <span>→</span>
          </span>
        </div>
      </article>
    </Link>
  );
};

const Ejendomme: FC = memo(() => {
  const propertiesResponse = fetchProperties();

  useGaTracker();
  useSeo({
    title: 'Ejendomme i Silkeborg | Toft Ejendomme',
    description:
      'Se et overblik over vores velholdte udlejningsejendomme i Silkeborg med central beliggenhed og høj boligkvalitet.',
    path: '/ejendomme',
  });

  return (
    <>
      <section className="page-masthead">
        <div className="section-wrap">
          <div className="page-masthead__grid fade-in">
            <div className="page-masthead__copy">
              <span className="eyebrow">Vores portefølje</span>
              <h1 className="page-title">Ejendomme i Silkeborg</h1>
              <p className="page-lead">
                Udforsk vores portefølje af kvalitetsejendomme i Silkeborg. Alle ejendomme er
                velvedligeholdte og placeret i områder, hvor det er let at få hverdagen til at hænge sammen.
              </p>
              <p className="page-lead">
                Se udvalgte ejendomme nedenfor, eller gå direkte til de aktuelle ledige lejemål.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="page-flow">
        <div className="section-wrap">
          <div className="property-grid">
            {propertiesResponse.data?.Ejendom?.map((property: PropertyProps) => (
              <PropertyCard key={property.ejendom.id} property={property} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
});

Ejendomme.displayName = 'Ejendomme';

export default Ejendomme;
